import api, { unwrap } from './client';
import type { ApiResponse, PageResponse } from '@/types/api';

// ─── Shift Types ─────────────────────────────────────────────────────────────

export type ShiftStatus = 'OPEN' | 'ACTIVE' | 'CLOSED';

export interface PosShift {
  id: number;
  storeId: number;
  counterId: number;
  employeeId: number;
  employeeName?: string;
  shiftStatus: ShiftStatus;
  openedAt: string;
  closedAt?: string;
  openingCash: number;
  closingCash?: number;
  expectedCash?: number;
  discrepancyAmount?: number;
  discrepancyReason?: string;
  cashSales: number;
  cardSales: number;
  upiSales: number;
  walletSales: number;
  razorpaySales: number;
  totalSales: number;
  totalRefunds: number;
  transactionCount: number;
  returnCount: number;
  heldBillCount: number;
  notes?: string;
  currencyCode: string;
  durationMinutes?: number;
}

export interface OpenShiftRequest {
  storeId: number;
  counterId: number;
  openingCash: number;
  notes?: string;
}

export interface CloseShiftRequest {
  closingCash: number;
  discrepancyReason?: string;
  notes?: string;
}

// ─── Analytics Types ──────────────────────────────────────────────────────────

export interface PosTopProduct {
  productId: number;
  productName: string;
  qtySold: number;
  revenue: number;
}

export interface PosAnalyticsDailySummary {
  storeId?: number;
  shiftId?: number;
  date?: string;
  totalSales: number;
  transactionCount: number;
  avgBasketSize: number;
  topProducts: PosTopProduct[];
}

export interface PosHourlySales {
  hour: number;
  transactionCount: number;
  totalSales: number;
}

// ─── Terminal Types ───────────────────────────────────────────────────────────

export type TerminalStatus = 'ONLINE' | 'OFFLINE' | 'IDLE';
export type HardwareType = 'TABLET' | 'DESKTOP' | 'MOBILE' | 'KIOSK';

export interface BillingCounter {
  id: number;
  storeId: number;
  name: string;
  code: string;
  openForBilling?: boolean;
  currentShiftId?: number;
  currentEmployeeId?: number;
  status?: boolean;
}

export interface PosTerminalResponse {
  id: number;
  storeId: number;
  counterId: number;
  deviceName: string;
  deviceId: string;
  terminalStatus: TerminalStatus;
  hardwareType: HardwareType;
  serialNumber?: string;
  lastActive?: string;
  registeredAt?: string;
}

// ─── API ─────────────────────────────────────────────────────────────────────

export const posApi = {
  // Shifts
  openShift: (body: OpenShiftRequest) =>
    unwrap(api.post<ApiResponse<PosShift>>('/v1/pos/shifts', body)),

  getActiveShift: (counterId: number) =>
    unwrap(api.get<ApiResponse<PosShift>>('/v1/pos/shifts/active', { params: { counterId } })),

  closeShift: (id: number, body: CloseShiftRequest) =>
    unwrap(api.post<ApiResponse<PosShift>>(`/v1/pos/shifts/${id}/close`, body)),

  getShift: (id: number) =>
    unwrap(api.get<ApiResponse<PosShift>>(`/v1/pos/shifts/${id}`)),

  listShifts: (storeId: number, from?: string, to?: string, page = 0, size = 20) =>
    unwrap(api.get<ApiResponse<PageResponse<PosShift>>>('/v1/pos/shifts', {
      params: { storeId, from, to, page, size },
    })),

  // Analytics
  getDailySummary: (storeId: number, date?: string) =>
    unwrap(api.get<ApiResponse<PosAnalyticsDailySummary>>('/v1/pos/analytics/daily', {
      params: { storeId, date },
    })),

  getHourlySales: (storeId: number, date?: string) =>
    unwrap(api.get<ApiResponse<PosHourlySales[]>>('/v1/pos/analytics/hourly', {
      params: { storeId, date },
    })),

  getAnalyticsFull: (storeId: number, date?: string) =>
    unwrap(
      api.get<
        ApiResponse<{
          daily: PosAnalyticsDailySummary;
          hourly: PosHourlySales[];
        }>
      >('/v1/pos/analytics/full', { params: { storeId, date } }),
    ),

  getShiftSummary: (shiftId: number) =>
    unwrap(api.get<ApiResponse<PosAnalyticsDailySummary>>(`/v1/pos/analytics/shift/${shiftId}`)),

  // Terminals
  registerTerminal: (body: {
    storeId: number;
    counterId?: number;
    deviceName?: string;
    deviceId: string;
    deviceType?: string;
  }) => unwrap(api.post<ApiResponse<PosTerminalResponse>>('/v1/pos/terminals/register', body)),

  listTerminals: (storeId: number, page = 0, size = 50) =>
    unwrap(
      api.get<ApiResponse<PageResponse<PosTerminalResponse>>>('/v1/devices', {
        params: { storeId, page, size },
      }),
    ),

  heartbeat: (terminalId: number) =>
    unwrap(api.post<ApiResponse<PosTerminalResponse>>(`/v1/pos/terminals/${terminalId}/heartbeat`)),

  goOffline: (terminalId: number) =>
    unwrap(api.post<ApiResponse<PosTerminalResponse>>(`/v1/pos/terminals/${terminalId}/offline`)),

  stripeConnectionToken: (storeId: number) =>
    unwrap(api.post<ApiResponse<{ secret: string }>>('/v1/pos/terminals/stripe/connection-token', null, { params: { storeId } })),

  stripeTerminalPaymentIntent: (params: { storeId: number; amount: number; currency?: string; invoiceId: number }) =>
    unwrap(
      api.post<ApiResponse<Record<string, unknown>>>('/v1/pos/terminals/stripe/payment-intent', null, {
        params: {
          storeId: params.storeId,
          amount: params.amount,
          currency: params.currency,
          invoiceId: params.invoiceId,
        },
      }),
    ),

  listCounters: (storeId: number, page = 0, size = 50) =>
    unwrap(
      api.get<ApiResponse<PageResponse<BillingCounter>>>('/v1/billing-counters', {
        params: { storeId, page, size, activeOnly: true },
      }),
    ),

  createCounter: (body: { storeId: number; name: string; code: string }) =>
    unwrap(api.post<ApiResponse<BillingCounter>>('/v1/billing-counters', body)),

  // Setup — payment gateways
  listGateways: (storeId: number, enabledOnly = false) =>
    unwrap(
      api.get<ApiResponse<StorePaymentGateway[]>>('/v1/pos/setup/gateways', {
        params: { storeId, enabledOnly },
      }),
    ),

  checkoutGateways: (storeId: number) =>
    unwrap(api.get<ApiResponse<StorePaymentGateway[]>>('/v1/pos/setup/gateways/checkout', { params: { storeId } })),

  saveGateway: (body: StorePaymentGatewayRequest) =>
    unwrap(api.post<ApiResponse<StorePaymentGateway>>('/v1/pos/setup/gateways', body)),

  deleteGateway: (id: number) => unwrap(api.delete<ApiResponse<void>>(`/v1/pos/setup/gateways/${id}`)),

  createCheckoutSession: (params: {
    storeId: number;
    provider: string;
    amount: number;
    currency?: string;
    invoiceId: number;
  }) => unwrap(api.post<ApiResponse<Record<string, unknown>>>('/v1/pos/payments/checkout-session', null, { params })),

  getHardwareConfig: (storeId: number, counterId?: number) =>
    unwrap(api.get<ApiResponse<PosHardwareConfig>>('/v1/pos/setup/hardware', { params: { storeId, counterId } })),

  saveHardwareConfig: (body: PosHardwareConfig) =>
    unwrap(api.put<ApiResponse<PosHardwareConfig>>('/v1/pos/setup/hardware', body)),

  generateBarcode: (productId: number, storeId?: number, format = 'CODE_128') =>
    unwrap(
      api.get<ApiResponse<Record<string, unknown>>>('/v1/pos/barcodes/generate', {
        params: { productId, storeId, format },
      }),
    ),

  createDisplaySession: (storeId: number, counterId?: number) =>
    unwrap(
      api.post<ApiResponse<{ sessionToken: string }>>('/v1/pos/display/session', null, {
        params: { storeId, counterId },
      }),
    ),

  updateDisplay: (token: string, cartJson: string, totalAmount: number) =>
    unwrap(api.put<ApiResponse<void>>(`/v1/pos/display/${token}`, { cartJson, totalAmount })),

  getDisplay: (token: string) =>
    unwrap(api.get<ApiResponse<{ cartJson?: string; totalAmount: number }>>(`/v1/pos/display/${token}`)),

  actApproval: (id: number, action: 'APPROVE' | 'REJECT', comments?: string) =>
    unwrap(
      api.post<ApiResponse<string>>(`/v1/pos/approvals/${id}/act`, null, {
        params: { action, comments },
      }),
    ),

  salesReport: (storeId: number, from: string, to: string) =>
    unwrap(api.get<ApiResponse<Record<string, unknown>>>('/v1/pos/reports/sales', { params: { storeId, from, to } })),

  collectionReport: (storeId: number, from: string, to: string) =>
    unwrap(
      api.get<ApiResponse<Record<string, unknown>>>('/v1/pos/reports/collection', {
        params: { storeId, from, to },
      }),
    ),

  returnReport: (storeId: number, from: string, to: string) =>
    unwrap(api.get<ApiResponse<Record<string, unknown>>>('/v1/pos/reports/returns', { params: { storeId, from, to } })),
};

export interface StorePaymentGateway {
  id: number;
  storeId: number;
  providerCode: string;
  displayName: string;
  enabled: boolean;
  defaultGateway: boolean;
  sortOrder: number;
  configJson?: string;
  testMode: boolean;
}

export interface StorePaymentGatewayRequest {
  storeId: number;
  providerCode: string;
  displayName: string;
  enabled?: boolean;
  defaultGateway?: boolean;
  sortOrder?: number;
  configJson?: string;
  testMode?: boolean;
}

export interface PosHardwareConfig {
  id?: number;
  storeId: number;
  counterId?: number;
  scaleEnabled?: boolean;
  scaleUnit?: string;
  customerDisplayEnabled?: boolean;
  barcodePrinterEnabled?: boolean;
  thermalPrinterEnabled?: boolean;
  cashDrawerEnabled?: boolean;
}
